import type { LLMStreamEvent, PublicRunSnapshot } from '../../../contracts';
import { readChildAgentRunMetadata } from './metadata';

export const CHILD_RUN_KIND = {
  computerUse: 'computer_use',
  recording: 'recording',
  fileTools: 'file_tools',
} as const;

export type ChildRunKind = (typeof CHILD_RUN_KIND)[keyof typeof CHILD_RUN_KIND];

export interface ChildRunDiscovery {
  childRunId: string;
  parentRunId: string;
  toolCallId?: string;
  restored?: true;
  /** Backend-pinned owner. Absent for legacy children. */
  targetId?: string;
}

type ChildRunSnapshot = Pick<PublicRunSnapshot, 'agent_run_id' | 'child_runs'>;

export const withUpsertedChildRunDiscovery = (
  current: ChildRunDiscovery[],
  discovery: ChildRunDiscovery
): ChildRunDiscovery[] => {
  const index = current.findIndex((item) => item.childRunId === discovery.childRunId);
  if (index === -1) return [...current, discovery];

  const next = [...current];
  next[index] = { ...current[index], ...discovery };
  return next;
};

export const discoverChildRunFromStreamEvent = (
  kind: ChildRunKind,
  parentRunId: string,
  event: LLMStreamEvent
): ChildRunDiscovery | null => {
  if (event.type !== 'tool_execution_start' || !event.tool_call_id) return null;

  const child = readChildAgentRunMetadata(event.metadata);
  if (child?.kind !== kind) return null;

  return {
    childRunId: child.childAgentRunId,
    parentRunId,
    toolCallId: event.tool_call_id,
    ...(child.targetId && { targetId: child.targetId }),
  };
};

export const discoverChildRunsFromSnapshot = (
  kind: ChildRunKind,
  snapshot: ChildRunSnapshot,
  restored = false
): ChildRunDiscovery[] =>
  (snapshot.child_runs ?? [])
    .filter((child) => child.kind === kind && child.status === 'running')
    .map((child) => ({
      childRunId: child.agent_run_id,
      parentRunId: snapshot.agent_run_id,
      ...(child.target_id && { targetId: child.target_id }),
      ...(restored ? { restored: true as const } : {}),
    }));

export const reconcileChildRunDiscoveriesFromSnapshot = (
  kind: ChildRunKind,
  current: ChildRunDiscovery[],
  snapshot: ChildRunSnapshot,
  restored = false
): ChildRunDiscovery[] => {
  const endedChildRunIds = new Set(
    (snapshot.child_runs ?? [])
      .filter((child) => child.kind === kind && child.status !== 'running')
      .map((child) => child.agent_run_id)
  );
  const running = discoverChildRunsFromSnapshot(kind, snapshot, restored);
  const currentById = new Map(current.map((d) => [d.childRunId, d]));

  const hasPrune = current.some((d) => endedChildRunIds.has(d.childRunId));
  const hasRunningDiscoveryUpdate = running.some((discovery) => {
    const existing = currentById.get(discovery.childRunId);
    return (
      !existing ||
      (discovery.targetId !== undefined && discovery.targetId !== existing.targetId)
    );
  });
  if (!hasPrune && !hasRunningDiscoveryUpdate) return current;

  const runningById = new Map(running.map((d) => [d.childRunId, d]));
  const next = current
    .filter((d) => !endedChildRunIds.has(d.childRunId))
    .map((d) => {
      const seen = runningById.get(d.childRunId);
      return seen ? { ...d, ...seen } : d;
    });
  for (const discovery of runningById.values()) {
    if (!currentById.has(discovery.childRunId)) next.push(discovery);
  }
  return next;
};
